import { EmptyState, RagBadge, EscalationBadge } from "@/components/widgets";
import { Badge } from "@/components/ui/badge";
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, Legend, ReferenceArea,
} from "recharts";
import { AlertTriangle, Flame } from "lucide-react";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { PanelCard } from "./primitives";
import { AXIS_TICK, CRIMSON, GOLD, LEGEND_STYLE, NAVY, TOOLTIP_STYLE, TREND_MARGIN } from "./constants";

export const EscalationMatrixPanel = ({ matrix, navigate }) => (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><Flame size={15} className="text-[hsl(var(--cx-crimson))]" />Escalation Matrix</span>}
    right={<button className="text-xs underline underline-offset-2 text-muted-foreground hover:text-foreground" onClick={() => navigate("/escalations")} data-testid="escalations-view-all">All {matrix.total.toLocaleString()}</button>}
    testId="escalation-matrix-panel"
    className="col-span-2 order-12 lg:order-none lg:col-span-6">
    {matrix.total === 0 ? (
      <EmptyState title="No open escalations" />
    ) : (
      <div className="px-3 pb-3 space-y-1">
        {matrix.levels.map((lv) => (
          <button
            key={lv.level}
            className={cn(
              "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md text-left transition-colors hover:bg-[hsl(var(--cx-surface))]",
              lv.level === 5 && lv.count > 0 && "bg-[hsl(var(--cx-crimson)/0.05)] border border-[hsl(var(--cx-crimson)/0.25)]"
            )}
            data-testid={`esc-level-row-${lv.level}`}
            onClick={() => navigate(`/complaints?escalation_level=${lv.level}`)}
          >
            <span className="flex items-center gap-2 min-w-0">
              <EscalationBadge level={lv.level} />
              <span className="text-xs truncate">{lv.label}</span>
            </span>
            <span className={cn("tabular text-sm font-semibold shrink-0",
              lv.count > 0 && lv.level >= 4 ? "text-[hsl(var(--cx-crimson))]" : "")}>
              {lv.count.toLocaleString()}
            </span>
          </button>
        ))}
        <p className="text-[0.65rem] text-muted-foreground px-2.5 pt-1">
          Automatic by aging · OEM delays excluded · L5 digest goes to MD daily · clears on closure in Salesforce
        </p>
      </div>
    )}
  </PanelCard>
);

export const OldestOpenPanel = ({ items, openComplaint }) => (
  <PanelCard title="Oldest 5 Open Complaints" testId="oldest-open-panel"
    className="col-span-2 order-3 lg:order-none lg:col-span-6">
    {items.length === 0 ? (
      <EmptyState title="Nothing open" />
    ) : (
      <div className="px-2 pb-3">
        {items.map((c) => (
          <button key={c.id} className="w-full text-left px-3 py-2 rounded-md hover:bg-[hsl(var(--cx-surface))] transition-colors"
            data-testid={`oldest-open-row-${c.complaint_number}`}
            onClick={() => openComplaint(c.id)}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{c.complaint_number}</span>
              <RagBadge rag={c.rag} days={c.days_open} />
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {c.outlet_name} · {c.category} · <span className="font-medium text-foreground">{c.owner || "unassigned"}</span>
            </p>
          </button>
        ))}
      </div>
    )}
  </PanelCard>
);

export const TrendPanel = ({ trend, shadeRuns, navigate }) => (
  <PanelCard title="12-Month Volume — opened vs closed" testId="trend-panel"
    className="col-span-2 order-[13] lg:order-none lg:col-span-8">
    <div className="h-[280px] px-4 pb-2">
      {trend.length ? (
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={trend} margin={TREND_MARGIN}
            onClick={(e) => {
              if (e?.activeLabel) {
                const from = `${e.activeLabel}-01`;
                const to = dayjs(from).endOf("month").format("YYYY-MM-DD");
                navigate(`/complaints?date_from=${from}&date_to=${to}`);
              }
            }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#EDF0F4" />
            <XAxis dataKey="month" tick={AXIS_TICK} />
            <YAxis tick={AXIS_TICK} />
            <RTooltip contentStyle={TOOLTIP_STYLE} />
            <Legend wrapperStyle={LEGEND_STYLE} />
            {shadeRuns.map(([x1, x2]) => (
              <ReferenceArea key={`${x1}-${x2}`} x1={x1} x2={x2} fill={CRIMSON} fillOpacity={0.05} strokeOpacity={0} />
            ))}
            <Line type="monotone" dataKey="opened" name="Opened" stroke={NAVY} strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="closed" name="Closed" stroke={GOLD} strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <EmptyState title="No data" />
      )}
    </div>
    <p className="px-5 pb-3 text-[0.68rem] text-muted-foreground">
      Closed line counts closures recorded in the system; historical Salesforce rows imported without a closure date
      appear only in the opened line. Shaded areas: inflow exceeded outflow. Click a month to drill down.
    </p>
  </PanelCard>
);

export const OutlierWatch = ({ outliers, navigate }) => (
  <PanelCard
    title={<span className="flex items-center gap-1.5"><AlertTriangle size={15} className="text-[hsl(var(--cx-crimson))]" />Outlier Watch</span>}
    testId="outlier-watch-panel"
    className="col-span-2 order-[14] lg:order-none lg:col-span-4">
    <div className="px-2 pb-3 max-h-[320px] overflow-y-auto">
      {outliers.length === 0 ? (
        <EmptyState title="No outliers" subtitle="All outlets within 1.5× of group average." />
      ) : (
        outliers.slice(0, 8).map((o, i) => (
          <button key={`${o.outlet_id}-${o.metric}-${o.function}`} className="w-full text-left px-3 py-2 rounded-md hover:bg-[hsl(var(--cx-surface))] transition-colors"
            data-testid={`outlier-item-${i}`}
            onClick={() => navigate(`/complaints?outlet_id=${o.outlet_id}&rag=Red&open=1`)}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{o.outlet_name}</span>
              <Badge variant="outline" className="text-[0.65rem]">{o.function}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">
              {o.metric === "red_count" ? "Red count" : "Avg resolution"}{" "}
              <span className="text-[hsl(var(--cx-crimson))] font-semibold tabular">{o.value}</span>{" "}
              vs avg {o.group_avg} ({(o.value / (o.group_avg || 1)).toFixed(1)}×)
            </p>
          </button>
        ))
      )}
    </div>
  </PanelCard>
);
